import { Box, Skeleton, Typography } from "@mui/material";
import Flex from "../../../../../components/Flex";
import {
  ENCOUNTER_KPI_CARDS,
  EncounterKPICardConfig,
} from "./patient-encounter-constants";
import { CHART_TYPOGRAPHY } from "../../../../../components/app/provider/patient/chart";

interface PatientEncounterKPICardsProps {
  data: Record<string, any> | undefined;
  isLoading: boolean;
}

const VALUE_BOX_STYLE = {
  backgroundColor: "#CDF4F8",
  border: "1px solid #AEE7ED",
  borderRadius: "4px",
  padding: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
};

const KPICard = ({
  config,
  data,
  isLoading,
}: {
  config: EncounterKPICardConfig;
  data: Record<string, any> | undefined;
  isLoading: boolean;
}) => {
  const value = data?.[config.valueKey] ?? 0;
  const change = config.changeKey ? data?.[config.changeKey] : undefined;
  const subtextLabel = config.subtext;

  return (
    <Box
      sx={{
        border: "1px solid #E7E7E7",
        borderRadius: "4px",
        padding: "8px 12px",
        flex: "1 1 0",
        minWidth: 200,
        backgroundColor: "#FFFFFF",
      }}
    >
      {isLoading ? (
        <Flex justifyContent="space-between" alignItems="center">
          <Box flex={1}>
            <Skeleton width="80%" height={18} />
            <Skeleton width="60%" height={14} sx={{ mt: 1 }} />
          </Box>
          <Skeleton width={50} height={35} variant="rounded" />
        </Flex>
      ) : (
        <Flex justifyContent="space-between" alignItems="center" gap="6px">
          <Box flex={1}>
            <Typography
              fontSize={CHART_TYPOGRAPHY.body5.fontSize}
              fontWeight={500}
              color="#373D41"
              noWrap
              lineHeight={1.2}
            >
              {config.label}
            </Typography>
            {subtextLabel && change !== undefined && (
              <Typography fontSize={11} color="#596063" mt={0.5}>
                {change > 0 ? `+${change}` : change}% {subtextLabel}
              </Typography>
            )}
            {config.key === "unsigned" &&
              data?.overdueUnsignedNotesCount !== undefined && (
                <Typography fontSize={11} color="#596063" mt={0.5}>
                  Overdue {data.overdueUnsignedNotesCount}
                </Typography>
              )}
          </Box>

          <Box sx={VALUE_BOX_STYLE}>
            <Typography
              fontSize={CHART_TYPOGRAPHY.body5.fontSize}
              fontWeight={600}
              color="#373D41"
            >
              {config.format === "number" ? value?.toLocaleString() : value}
            </Typography>
            {config.format === "percent" && (
              <Typography
                fontSize={CHART_TYPOGRAPHY.body5.fontSize}
                fontWeight={500}
                color="#373D41"
              >
                %
              </Typography>
            )}
          </Box>
        </Flex>
      )}
    </Box>
  );
};

const PatientEncounterKPICards = ({
  data,
  isLoading,
}: PatientEncounterKPICardsProps) => {
  return (
    <Flex gap="12px" flexWrap="wrap">
      {ENCOUNTER_KPI_CARDS.map((card) => (
        <KPICard
          key={card.key}
          config={card}
          data={data}
          isLoading={isLoading}
        />
      ))}
    </Flex>
  );
};

export default PatientEncounterKPICards;
